import React from "react";

import { Field, Label, Description, ErrorMessage } from "V2.0/common/fieldset";
import { Select } from "V2.0/common/select";
import { CheckboxField, Checkbox } from "V2.0/common/checkbox";
import { SwitchField, Switch } from "V2.0/common/switch";
import { Input } from "V2.0/common/input";

import { MOMENT_OPTIONS, TriggerEvent, showsCallOnce, showsCallStatusValues } from "../models/sections";

type Props = {
  triggerEvent: TriggerEvent;
  callDirection: string[];
  callOnce: boolean;
  callStatusMatchValues: string;
  errors: Record<string, string>;
  onChange: (patch: {
    triggerEvent?: TriggerEvent;
    callDirection?: string[];
    callOnce?: boolean;
    callStatusMatchValues?: string;
  }) => void;
};

const DIRECTIONS = [
  { value: "inbound", label: "Incoming calls" },
  { value: "outbound", label: "Outgoing calls" },
];

export function WhenItRuns({ triggerEvent, callDirection, callOnce, callStatusMatchValues, errors, onChange }: Props) {
  const moment = MOMENT_OPTIONS.find((option) => option.value === triggerEvent) ?? MOMENT_OPTIONS[1];

  const toggleDirection = (value: string, checked: boolean) => {
    const next = checked ? Array.from(new Set([...callDirection, value])) : callDirection.filter((item) => item !== value);
    onChange({ callDirection: next });
  };

  return (
    <>
      <Field>
        <Label>Runs</Label>
        <Select name="triggerEvent" value={triggerEvent} onChange={(event) => onChange({ triggerEvent: event.target.value as TriggerEvent })}>
          {MOMENT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <Description>{moment.description}</Description>
      </Field>

      <div className="flex flex-col gap-3">
        <p className="text-base/6 font-medium text-zinc-950 sm:text-sm/6">Applies to</p>
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          {DIRECTIONS.map((direction) => (
            <CheckboxField key={direction.value}>
              <Checkbox
                color="blue"
                checked={callDirection.includes(direction.value)}
                onChange={(checked) => toggleDirection(direction.value, checked)}
              />
              <Label>{direction.label}</Label>
            </CheckboxField>
          ))}
        </div>
        {errors.callDirection && <p className="text-base/6 text-red-600 sm:text-sm/6">{errors.callDirection}</p>}
      </div>

      {showsCallOnce(triggerEvent) && (
        <SwitchField>
          <Label>Run at most once per call</Label>
          <Description>Off, it can run again later in the same call whenever the checks pass again.</Description>
          <Switch color="blue" checked={callOnce} onChange={(checked) => onChange({ callOnce: checked })} />
        </SwitchField>
      )}

      {showsCallStatusValues(triggerEvent) && (
        <Field>
          <Label>Which call statuses</Label>
          <Description>Runs when the call reaches one of these. Separate several with commas, for example completed, no-answer, busy.</Description>
          <Input
            name="callStatusMatchValues"
            value={callStatusMatchValues}
            onChange={(event) => onChange({ callStatusMatchValues: event.target.value })}
            placeholder="completed, no-answer"
          />
          {errors.callStatusMatchValues && <ErrorMessage>{errors.callStatusMatchValues}</ErrorMessage>}
        </Field>
      )}
    </>
  );
}
